import React, { useMemo, forwardRef, useRef, useImperativeHandle } from 'react';
import { CellFloat, CellFloatV8, TablePaginatedV8 } from 'motion-components';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import FooterItem from './footer/FooterItem';
import { formatQty } from 'shared-pages/common/number-helpfunc';
/*------------------------------------------- TYPES -------------------------------------------*/
type CollectorBreakdownRow = {
  collectorId: string;
  collectorName: string;
  collectionsCount: number;
  totalWeight: number;
  avgWeightPerCollection: number;
  firstCollection: Date | null;
  lastCollection: Date | null;
};
export type MaterialStats = {
  materialId: string;
  materialName: string;
  collectionsCount: number;
  firstCollection: Date | null;
  firstCollectionCode: string | null;
  lastCollection: Date | null;
  lastCollectionCode: string | null;
  totalQty: number;
  totalWeight: number;
  avgWeightPerCollection: number;
  collectorBreakdown: CollectorBreakdownRow[];
};
interface GridPageProps {
  data: MaterialStats | null;
  loading?: boolean;
  selected?: any;
  setSelected?: (row: any) => void;
  onPageChange?: (page: number) => void;
  dataCount?: number;
  initPageIndex?: number;
  onOrderChange?: (order: any) => void;
  initOrderBy?: string;
  appStore: any;
  tableStore: any;
}
/*----------------------------------------------------------------------------------------------*/

/**
 * Collector‑level breakdown grid for a single material.
 */
const GridPage = forwardRef<any, GridPageProps>(
  (
    {
      data,
      loading = false,
      selected,
      setSelected,
      onPageChange,
      dataCount,
      initPageIndex,
      onOrderChange,
      initOrderBy,
      appStore,
      tableStore,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const tableRef = useRef(null);
    useImperativeHandle(ref, () => tableRef.current);

    const { collectorBreakdown = [], ...stats } = data;

    /* ------------------------------------------------------------------ */
    /* Footer totals                                                      */
    /* ------------------------------------------------------------------ */

    /* helper: date (CODE) */
    const fmtDateCode = (dateRaw: any, code?: string) => {
      if (!dateRaw && !code) return '';
      let dateStr = '';
      try {
        dateStr = dateRaw ? format(new Date(dateRaw), 'dd/MM/yyyy') : '';
      } catch {
        dateStr = dateRaw ?? '';
      }
      return code ? `${dateStr} (${code})` : dateStr;
    };

    /* ------------------------------------------------------------------ */
    /* Columns                                                            */
    /* ------------------------------------------------------------------ */
    const columns = useMemo(
      () => [
        {
          id: 'collectorName',
          header: t('Συλλέκτης'),
          accessorKey: 'collectorName',
          footer: () => <FooterItem>{t('Σύνολο')}</FooterItem>,
          size: 40,
        },
        {
          id: 'collectionsCount',
          header: t('Συλλογές'),
          accessorKey: 'collectionsCount',
          footer: () => <FooterItem>{stats.collectionsCount} </FooterItem>,
          size: 18,
        },
        {
          id: 'totalWeight',
          header: t('Συνολικό Βάρος (kg)'),
          accessorKey: 'totalWeight',
          // cell: ({ getValue }) => {
          //   const v = getValue();
          //   return v ? `${formatQty(v)} kg` : '-';
          // },
          // Factory: πρέπει να καλείται για να επιστρέψει τον cell renderer
          cell: CellFloatV8(),
          footer: () => (
            <FooterItem>{CellFloatV8()({ getValue: () => stats?.totalWeight })}</FooterItem>
          ),
          size: 20,
        },
        {
          id: 'avgWeightPerCollection',
          header: t('Μέσο Βάρος (Kg) / Συλλογή'),
          accessorKey: 'avgWeightPerCollection',
          cell: CellFloatV8(),
          footer: () => (
            <FooterItem>
              {CellFloatV8()({ getValue: () => stats?.avgWeightPerCollection })}
            </FooterItem>
          ),
          size: 22,
        },
        {
          id: 'firstCollection',
          header: t('Ημερομηνία Πρώτης Συλλογής'),
          size: 35,
          cell: ({ row }) => fmtDateCode(row.original.firstCollection),
          footer: () => (
            <FooterItem>
              {fmtDateCode(stats?.firstCollection, stats?.firstCollectionCode)}
            </FooterItem>
          ),
        },
        {
          id: 'lastCollectionInfo',
          header: t('Ημερομηνία Τελευταίας Συλλογής'),
          size: 35,
          cell: ({ row }) => fmtDateCode(row.original.lastCollection),
          footer: () => (
            <FooterItem>{fmtDateCode(stats.firstCollection, stats.firstCollectionCode)}</FooterItem>
          ),
        },
      ],
      [t, stats],
    );

    /* ------------------------------------------------------------------ */
    /* Row highlight                                                      */
    /* ------------------------------------------------------------------ */
    const rowProps = (row: any) => {
      if (!selected) return {};
      if (row.original?.collectorId === selected.collectorId) {
        return { style: { background: '#e3f2fd' } };
      }
      return {};
    };

    return (
      <TablePaginatedV8
        ref={tableRef}
        loading={loading}
        id="material-collector-grid"
        appStore={appStore}
        tableStore={tableStore}
        columns={columns as any}
        data={collectorBreakdown ?? []}
        dataCount={collectorBreakdown?.length ?? 0}
        topBorderRadius={false}
        onRowClick={setSelected ? (t) => setSelected(t.row.original) : undefined}
        rowProps={rowProps}
        onPageChange={onPageChange}
        initPageIndex={initPageIndex}
        initSorting={initOrderBy ? [{ id: initOrderBy, desc: false }] : undefined}
        onSortByChange={onOrderChange}
        showFooter
        showGridFooter
      />
    );
  },
);

export default GridPage;
